import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiRefreshCw, FiX } from "react-icons/fi";
import { useAuth } from "../../firebase/AuthContext";
import { getApiUrl } from "../../config/api";
import { CycleCard, WeekCard } from "./UsageSummary";
import SpendBreakdown from "./SpendBreakdown";
import WeeklyHistoryChart from "./WeeklyHistoryChart";
import {
  fillWeeks,
  formatDay,
  formatTwdFromUsd,
  formatUsdWithCode,
  resetDay,
} from "./usageFormat";
import useUsdTwdRate from "./useUsdTwdRate";
import UsageCurrencySwitch from "./UsageCurrencySwitch";

// How many finished weeks to ask for and chart.
const HISTORY_WEEKS = 8;

// The signed-in user's AI usage: this week against its quota, this 28-day
// cycle against the monthly limit, where the money went, and past weeks.
// Opened from the sidebar's profile menu and from the TG PIMS Agent chat box;
// the parent mounts it while open, and it loads GET /me each time.
//
// Rendered into document.body: the sidebar is a fixed, transformed element on
// mobile, which would otherwise trap this fixed overlay inside it.
export default function MyUsageModal({ onClose, language }) {
  const { currentUser } = useAuth();
  const zh = language === "zh";
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const rateState = useUsdTwdRate();
  const { data: exchangeRate, refresh: refreshRate } = rateState;
  const twdRate = exchangeRate?.rate;
  const formatConvertedMoney = useCallback(
    (value) => formatTwdFromUsd(value, twdRate),
    [twdRate],
  );
  const displayCurrency = currency === "TWD" && exchangeRate ? "TWD" : "USD";
  const formatMoney =
    displayCurrency === "TWD" ? formatConvertedMoney : formatUsdWithCode;

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/me?weeks=${HISTORY_WEEKS}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setUsage(data);
      } else {
        setError(data.error || `Request failed (${response.status})`);
      }
    } catch (err) {
      console.error("Failed to load usage:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const weeks = usage ? fillWeeks(usage, usage.history, HISTORY_WEEKS) : [];
  const neverUsed = usage && usage.turns === 0 && usage.history.length === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="my-usage-title"
        className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2
              id="my-usage-title"
              className="text-lg font-bold text-gray-900 dark:text-white"
            >
              {zh ? "我的 AI 用量" : "My AI usage"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {zh
                ? "TG PIMS Agent 的 AI 使用量 · 額度以 28 天為一個週期"
                : "AI use in TG PIMS Agent · limits run per 28-day cycle"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                loadUsage();
                refreshRate();
              }}
              disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title={zh ? "重新整理" : "Refresh"}
              aria-label={zh ? "重新整理" : "Refresh"}
            >
              <FiRefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label={zh ? "關閉" : "Close"}
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-4">
          <UsageCurrencySwitch
            currency={displayCurrency}
            onChange={setCurrency}
            rateState={rateState}
            language={language}
          />
          {!usage && loading && (
            <div className="flex flex-col items-center gap-3 py-16">
              <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {zh ? "正在載入用量..." : "Loading your usage..."}
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              <span>
                {zh ? "無法載入用量：" : "Couldn't load your usage: "}
                {error}
              </span>
              <button
                type="button"
                onClick={loadUsage}
                disabled={loading}
                className="shrink-0 font-medium underline disabled:opacity-50"
              >
                {zh ? "重試" : "Retry"}
              </button>
            </div>
          )}

          {/* On refresh the last result stays, dimmed, rather than flashing a spinner. */}
          {usage && (
            <div
              className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <WeekCard
                  week={usage}
                  enforced={usage.enforced}
                  language={language}
                  formatMoney={formatMoney}
                />
                <CycleCard
                  usage={usage}
                  language={language}
                  formatMoney={formatMoney}
                />
              </div>

              {neverUsed ? (
                <p className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {zh
                    ? "尚無 AI 使用紀錄。您在 TG PIMS Agent 提問後，用量會顯示在這裡。"
                    : "No AI usage yet. Your usage shows up here after your first question in TG PIMS Agent."}
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-5">
                  <div className="min-w-0 md:col-span-2">
                    <SpendBreakdown
                      breakdown={usage.breakdown_usd}
                      title={zh ? "本週費用明細" : "Where it went this week"}
                      language={language}
                      formatMoney={formatMoney}
                    />
                  </div>
                  <section className="min-w-0 md:col-span-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {zh ? "過去幾週" : "Past weeks"}
                    </h3>
                    {weeks.length > 1 ? (
                      <WeeklyHistoryChart
                        weeks={weeks}
                        language={language}
                        formatMoney={formatMoney}
                      />
                    ) : (
                      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {zh
                          ? `過去幾週的用量將於${formatDay(resetDay(usage.week_end), language)}起顯示在這裡。`
                          : `Past weeks appear here from ${formatDay(resetDay(usage.week_end), language)}.`}
                      </p>
                    )}
                  </section>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {zh
                  ? "運作方式：每月額度涵蓋一個 28 天的週期，並按週分配：每週可使用四分之一，加上本週期前幾週未用完的部分。每週從週四開始，於台北時間 00:00 重置；新週期不會結轉。"
                  : "How it works: your monthly limit covers one 28-day cycle and is paced weekly. Each week gets a quarter of it, plus whatever went unused earlier in the cycle. Weeks start on Thursday at 00:00 Taipei time; a new cycle starts fresh."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
